import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { MessageService } from 'primeng/api';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { ToastModule } from 'primeng/toast';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, catchError, debounceTime, forkJoin, from, map, of, switchMap } from 'rxjs';
import { PriceBook, PriceItem, PriceItemImportResult } from '@app/api/price-service';
import { FlashSaleApiService } from '@app/api/price-service/flash-sale-api.service';
import { PhysicalShopControllerService } from '@app/api/channel-service/api/physicalShopController.service';
import { PhysicalShopVO } from '@app/api/channel-service/model/physicalShopVO';
import { VendorControllerService } from '@app/api/vendor-service/api/vendorController.service';
import { PermissionService } from '@app/core/services/permission.service';
import { AdvancedDatepickerComponent } from '@shared/components/advanced-datepicker/advanced-datepicker.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import {
  FileUploadComponent,
  FileUploadData,
} from '@shared/components/file-upload/file-upload.component';
import { BlobConverterUtil } from '@shared/utils/blob-converter.util';
import { PriceListService } from './services/price-list.service';
import { buildPriceImportTemplateXlsx } from './utils/price-import-template.util';
import { MultiSelectModule } from 'primeng/multiselect';
import { CheckboxModule } from 'primeng/checkbox';
import { TooltipModule } from 'primeng/tooltip';

interface PriceTab {
  code: string;
  bookCode: string;
  labelKey: string;
  hintKey: string;
  types: string[];
  allowRule: boolean;
  allowCreate: boolean;
  showDates: boolean;
  showRule: boolean;
  showPublished: boolean;
  showProgram: boolean;
  showChannel?: boolean;
  showActions: boolean;
  pinned: boolean;
}

const TABS: PriceTab[] = [
  {
    code: 'COST_DEFAULT',
    bookCode: 'COST_DEFAULT',
    labelKey: 'price_list.tabs.cost',
    hintKey: 'price_list.hints.cost',
    types: ['COST'],
    allowRule: false,
    allowCreate: true,
    showDates: false,
    showRule: false,
    showPublished: false,
    showProgram: false,
    showActions: true,
    pinned: true,
  },
  {
    code: 'CLEARANCE',
    bookCode: 'RETAIL_DEFAULT',
    labelKey: 'price_list.tabs.clearance',
    hintKey: 'price_list.hints.clearance',
    types: ['CLEARANCE'],
    allowRule: true,
    allowCreate: true,
    showDates: true,
    showRule: true,
    showPublished: false,
    showProgram: false,
    showActions: true,
    pinned: true,
  },
  {
    code: 'RETAIL_DEFAULT',
    bookCode: 'RETAIL_DEFAULT',
    labelKey: 'price_list.tabs.retail',
    hintKey: 'price_list.hints.retail',
    types: ['BASE'],
    allowRule: true,
    allowCreate: true,
    showDates: true,
    showRule: true,
    showPublished: true,
    showProgram: false,
    showActions: true,
    pinned: true,
  },
  {
    code: 'WEB',
    bookCode: 'WEB',
    labelKey: 'price_list.tabs.web',
    hintKey: 'price_list.hints.web',
    types: ['BASE', 'CHANNEL_OVERRIDE'],
    allowRule: true,
    allowCreate: true,
    showDates: true,
    showRule: true,
    showPublished: false,
    showProgram: false,
    showActions: true,
    pinned: false,
  },
  {
    code: 'POS',
    bookCode: 'POS',
    labelKey: 'price_list.tabs.pos',
    hintKey: 'price_list.hints.pos',
    types: ['BASE', 'CHANNEL_OVERRIDE'],
    allowRule: true,
    allowCreate: true,
    showDates: true,
    showRule: true,
    showPublished: false,
    showProgram: false,
    showActions: true,
    pinned: false,
  },
  {
    code: 'MARKETPLACE',
    bookCode: 'MARKETPLACE',
    labelKey: 'price_list.tabs.marketplace',
    hintKey: 'price_list.hints.marketplace',
    types: ['CHANNEL_OVERRIDE'],
    allowRule: false,
    allowCreate: false,
    showDates: false,
    showRule: false,
    showPublished: false,
    showProgram: false,
    showChannel: true,
    showActions: true,
    pinned: true,
  },
  {
    code: 'WHOLESALE',
    bookCode: 'WHOLESALE',
    labelKey: 'price_list.tabs.wholesale',
    hintKey: 'price_list.hints.wholesale',
    types: ['WHOLESALE'],
    allowRule: true,
    allowCreate: true,
    showDates: true,
    showRule: true,
    showPublished: false,
    showProgram: false,
    showActions: true,
    pinned: false,
  },
  {
    code: 'DISTRIBUTION',
    bookCode: 'DISTRIBUTION',
    labelKey: 'price_list.tabs.distribution',
    hintKey: 'price_list.hints.distribution',
    types: ['DISTRIBUTION'],
    allowRule: true,
    allowCreate: true,
    showDates: true,
    showRule: true,
    showPublished: false,
    showProgram: false,
    showActions: true,
    pinned: false,
  },
  {
    code: 'FLASH_SALE',
    bookCode: 'RETAIL_DEFAULT',
    labelKey: 'price_list.tabs.flash_sale',
    hintKey: 'price_list.hints.flash_sale',
    types: ['FLASH_SALE'],
    allowRule: false,
    allowCreate: false,
    showDates: true,
    showRule: false,
    showPublished: false,
    showProgram: true,
    showChannel: false,
    showActions: true,
    pinned: true,
  },
  {
    code: 'MARKET',
    bookCode: 'RETAIL_DEFAULT',
    labelKey: 'price_list.tabs.market',
    hintKey: 'price_list.hints.market',
    types: ['MARKET'],
    allowRule: true,
    allowCreate: true,
    showDates: true,
    showRule: true,
    showPublished: false,
    showProgram: false,
    showChannel: false,
    showActions: true,
    pinned: false,
  },
];

const CHANGE_REASONS = [
  'COMPETITOR_MATCH',
  'SEASONAL_ADJUSTMENT',
  'COST_CHANGE',
  'PROMOTION_SUPPORT',
  'CLEARANCE_STOCK',
  'NEW_PRODUCT_LAUNCH',
  'CHANNEL_ALIGNMENT',
  'MANAGEMENT_DIRECTIVE',
] as const;

@Component({
  selector: 'app-price-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    MultiSelectModule,
    CheckboxModule,
    DatePickerModule,
    AdvancedDatepickerComponent,
    PaginatorModule,
    ToastModule,
    RouterLink,
    CardModule,
    FileUploadComponent,
    AppButtonComponent,
    TooltipModule,
  ],
  providers: [MessageService],
  templateUrl: './price-list.component.html',
  styleUrl: './price-list.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PriceListComponent implements OnInit {
  @ViewChild('importFileUpload') importFileUpload?: FileUploadComponent;

  private readonly priceListService = inject(PriceListService);
  private readonly flashSaleApi = inject(FlashSaleApiService);
  private readonly physicalShopApi = inject(PhysicalShopControllerService);
  private readonly vendorApi = inject(VendorControllerService);
  private readonly route = inject(ActivatedRoute);
  private readonly formBuilder = inject(FormBuilder);
  private readonly messages = inject(MessageService);
  private readonly translate = inject(TranslateService);
  private readonly permissionService = inject(PermissionService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly searchInput = new Subject<void>();
  /** vendorId → "CODE – Name" for COST tab */
  private readonly vendorLabels = signal<Record<number, string>>({});

  private readonly enabledSurfaces = signal<Set<string>>(
    new Set(['WEB', 'POS', 'WHOLESALE', 'DISTRIBUTION', 'MARKET']),
  );
  readonly visibleTabs = computed(() =>
    TABS.filter((tab) => tab.pinned || this.enabledSurfaces().has(tab.code)),
  );
  readonly books = signal<PriceBook[]>([]);
  readonly activeCode = signal('COST_DEFAULT');
  readonly items = signal<PriceItem[]>([]);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly page = signal(0);
  readonly pageSize = signal(20);
  readonly rowsPerPageOptions = [20, 50, 100];
  readonly goToPageNumber = signal(1);
  readonly total = signal(0);
  readonly maxPage = computed(
    () => Math.ceil(this.total() / this.pageSize()) || 1,
  );
  readonly skuNameFilter = signal('');
  readonly skuCodeFilter = signal('');
  readonly amountFrom = signal<number | null>(null);
  readonly amountTo = signal<number | null>(null);
  readonly filterStartDate = signal<Date | null>(null);
  readonly filterEndDate = signal<Date | null>(null);
  readonly statusFilter = signal<string | null>(null);
  readonly statusFilterOptions = [
    { value: null as string | null, labelKey: 'price_list.filters.status_all' },
    { value: 'ACTIVE', labelKey: 'price_list.filters.status_active' },
    { value: 'INACTIVE', labelKey: 'price_list.filters.status_inactive' },
    { value: 'PENDING', labelKey: 'price_list.filters.status_pending' },
  ];
  readonly dialogOpen = signal(false);
  readonly saving = signal(false);
  readonly editingItem = signal<PriceItem | null>(null);
  readonly canEditBasePrice = signal(false);
  readonly canCreatePrice = signal(false);
  readonly canDeletePrice = signal(false);
  readonly canApprovePrice = signal(false);
  readonly canCancelPending = signal(false);
  readonly canDeactivatePrice = signal(false);
  readonly approving = signal(false);
  readonly selectedSkuCost = signal<number | null>(null);
  readonly selectedSkuCostLoading = signal(false);
  /** Published BASE retail price (RETAIL_DEFAULT) of the selected SKU. */
  readonly selectedSkuRetail = signal<number | null>(null);
  readonly selectedSkuRetailLoading = signal(false);
  readonly minSelectableDate = (() => {
    const date = new Date();
    date.setSeconds(0, 0);
    return date;
  })();

  readonly activeBook = computed(() =>
    this.books().find((book) => book.code === this.activeTab().bookCode),
  );
  readonly activeTab = computed(
    () =>
      this.visibleTabs().find((tab) => tab.code === this.activeCode()) ??
      this.visibleTabs()[0],
  );
  readonly showBaseActions = computed(
    () => this.activeCode() === 'RETAIL_DEFAULT' && this.canEditBasePrice(),
  );
  readonly showDates = computed(() => this.activeTab().showDates);
  readonly showRule = computed(() => this.activeTab().showRule);
  readonly showPublished = computed(() => this.activeTab().showPublished);
  readonly showMinQty = computed(() => this.activeCode() === 'WHOLESALE');
  readonly showClearanceScope = computed(() => this.activeCode() === 'CLEARANCE');
  readonly showMarketSource = computed(() => this.activeCode() === 'MARKET');
  readonly showProgram = computed(() => this.activeTab().showProgram);
  readonly showChannel = computed(() => !!this.activeTab().showChannel);
  readonly showActions = computed(() => this.activeTab().showActions);
  readonly allowCreate = computed(
    () => this.activeTab().allowCreate && this.canCreatePrice(),
  );
  readonly editingBase = computed(
    () =>
      this.editingItem()?.priceType === 'BASE' &&
      this.activeCode() === 'RETAIL_DEFAULT',
  );
  /** Tabs that support multi-select + bulk actions. */
  readonly showBulkSelection = computed(() =>
    [
      'COST_DEFAULT',
      'CLEARANCE',
      'RETAIL_DEFAULT',
      'WEB',
      'POS',
      'WHOLESALE',
      'DISTRIBUTION',
      'MARKET',
      'FLASH_SALE',
      'MARKETPLACE',
    ].includes(this.activeCode()),
  );
  /** Deactivate only for sellable catalog tabs (not promo / marketplace). */
  readonly showBulkDeactivate = computed(() =>
    [
      'COST_DEFAULT',
      'CLEARANCE',
      'RETAIL_DEFAULT',
      'WEB',
      'POS',
      'WHOLESALE',
      'DISTRIBUTION',
      'MARKET',
    ].includes(this.activeCode()),
  );
  /** Checkbox column and bulk toolbar only when the user can run at least one action. */
  readonly showBulkPicker = computed(
    () =>
      (this.showBulkSelection() && this.canApprovePrice()) ||
      (this.showBulkDeactivate() && this.canDeactivatePrice()) ||
      (this.showBulkSelection() && this.canCancelPending()) ||
      (this.showBulkSelection() && this.canDeletePrice()),
  );
  readonly showClearanceScopeColumn = computed(() => this.activeCode() === 'CLEARANCE');

  readonly selectedIds = signal<Set<number>>(new Set());
  readonly bulkBusy = signal(false);
  readonly bulkConfirmOpen = signal(false);
  readonly bulkConfirmAction = signal<
    'approve' | 'deactivate' | 'cancel' | 'delete' | null
  >(null);
  readonly importDialogOpen = signal(false);
  readonly importBusy = signal(false);
  readonly importUpload = signal<FileUploadData | null>(null);

  readonly selectedItems = computed(() => {
    const ids = this.selectedIds();
    return this.items().filter((item) => ids.has(item.id));
  });
  readonly canBulkApprove = computed(() =>
    this.selectedItems().some((item) => this.isBulkApprovable(item)),
  );
  readonly canBulkDeactivate = computed(() =>
    this.selectedItems().some((item) => this.isBulkDeactivatable(item)),
  );
  readonly canBulkCancel = computed(() =>
    this.selectedItems().some((item) => this.isBulkCancellable(item)),
  );
  readonly canBulkDelete = computed(() =>
    this.selectedItems().some((item) => this.isBulkDeletable(item)),
  );
  readonly bulkConfirmEligibleCount = computed(() => {
    const action = this.bulkConfirmAction();
    if (!action) {
      return 0;
    }
    if (action === 'approve') {
      return this.selectedItems().filter((item) => this.isBulkApprovable(item)).length;
    }
    if (action === 'deactivate') {
      return this.selectedItems().filter((item) => this.isBulkDeactivatable(item)).length;
    }
    if (action === 'delete') {
      return this.selectedItems().filter((item) => this.isBulkDeletable(item)).length;
    }
    return this.selectedItems().filter((item) => this.isBulkCancellable(item)).length;
  });
  readonly allPageSelected = computed(() => {
    const rows = this.items();
    if (!rows.length || !this.showBulkPicker()) {
      return false;
    }
    const ids = this.selectedIds();
    return rows.every((item) => ids.has(item.id));
  });

  readonly columnCount = computed(() => {
    let count = 4;
    if (this.showBulkPicker()) {
      count += 1;
    }
    if (this.activeCode() === 'COST_DEFAULT') {
      count += 1;
    }
    if (this.showProgram()) {
      count += 1;
    }
    if (this.showChannel()) {
      count += 1;
    }
    if (this.showClearanceScopeColumn()) {
      count += 1;
    }
    if (this.showMinQty()) {
      count += 1;
    }
    if (this.showDates()) {
      count += 2;
    }
    if (this.showRule()) {
      count += 1;
    }
    if (this.showPublished()) {
      count += 1;
    }
    if (this.showActions()) {
      count += 1;
    }
    return count;
  });
  readonly ruleOptions = computed(() => {
    const code = this.activeCode();
    let types: string[] = ['FIXED', 'MARKUP_ON_COST', 'MARKDOWN_ON_BASE'];
    if (code === 'RETAIL_DEFAULT') {
      types = ['FIXED', 'MARKUP_ON_COST'];
    } else if (code === 'MARKET') {
      types = ['FIXED'];
    } else if (
      code === 'CLEARANCE' ||
      code === 'WHOLESALE' ||
      code === 'DISTRIBUTION' ||
      code === 'WEB' ||
      code === 'POS'
    ) {
      types = ['FIXED', 'MARKUP_ON_COST', 'MARKDOWN_ON_BASE'];
    }
    return types.map((value) => ({
      label: this.translate.instant(`price_list.rules.${value}`),
      value,
    }));
  });

  /** Tabs that show published retail under cost and require amount &lt; retail. */
  readonly showRetailReference = computed(() =>
    ['CLEARANCE', 'WHOLESALE', 'DISTRIBUTION'].includes(this.activeCode()),
  );

  /** Bumps when the dialog form changes so canSaveForm stays reactive. */
  readonly formTick = signal(0);

  /** Create mode: lock all fields until SKU is chosen. */
  readonly fieldsLockedUntilSku = computed(() => {
    void this.formTick();
    return !this.editingItem() && !this.form.controls.skuCode.value;
  });

  readonly canSaveForm = computed(() => {
    void this.formTick();
    void this.selectedSkuRetail();
    void this.saving();
    if (this.saving()) {
      return false;
    }
    if (!this.editingItem() && !this.form.controls.skuCode.value) {
      return false;
    }
    if (
      this.showDates() &&
      !this.editingFlashSale() &&
      !this.form.controls.startAt.value
    ) {
      return false;
    }
    if (this.showMinQty() && this.form.controls.minQty.invalid) {
      return false;
    }
    const ruleType = this.form.controls.ruleType.value;
    if (!this.activeTab().allowRule || ruleType === 'FIXED') {
      if (this.form.controls.amount.invalid || this.form.controls.amount.value == null) {
        return false;
      }
    } else if (
      this.form.controls.ratePercent.invalid ||
      this.form.controls.ratePercent.value == null
    ) {
      return false;
    }
    if (
      this.showClearanceScope() &&
      this.form.controls.scopeType.value === 'SELECTED_STORES' &&
      !(this.form.controls.physicalShopIds.value?.length > 0)
    ) {
      return false;
    }
    if (
      this.showRetailReference() &&
      this.form.controls.skuCode.value &&
      this.selectedSkuRetail() == null &&
      !this.selectedSkuRetailLoading()
    ) {
      return false;
    }
    if (this.activeCode() === 'COST_DEFAULT') {
      const amount = Number(this.form.controls.amount.value);
      if (!(amount > 0)) {
        return false;
      }
    }
    return true;
  });

  readonly reasonOptions = computed(() =>
    CHANGE_REASONS.map((value) => ({
      label: this.translate.instant(`price_list.reasons.${value}`),
      value,
    })),
  );

  readonly scopeOptions = computed(() => [
    {
      label: this.translate.instant('price_list.scope.all_stores_and_web'),
      value: 'ALL_STORES_AND_WEB',
    },
    {
      label: this.translate.instant('price_list.scope.web_only'),
      value: 'WEB_ONLY',
    },
    {
      label: this.translate.instant('price_list.scope.selected_stores'),
      value: 'SELECTED_STORES',
    },
  ]);

  readonly skuOptions = signal<{ skuCode: string; skuName: string }[]>([]);
  readonly skuOptionsLoading = signal(false);
  readonly shopOptions = signal<{ id: number; name: string; code: string }[]>([]);

  readonly form = this.formBuilder.group({
    skuCode: this.formBuilder.nonNullable.control('', Validators.required),
    priceType: this.formBuilder.nonNullable.control('BASE', Validators.required),
    amount: this.formBuilder.control<number | null>(null, [
      Validators.required,
      Validators.min(0),
    ]),
    minQty: this.formBuilder.nonNullable.control(50, [
      Validators.required,
      Validators.min(1),
    ]),
    ruleType: this.formBuilder.nonNullable.control('FIXED'),
    ratePercent: this.formBuilder.nonNullable.control(0, [Validators.min(0)]),
    status: this.formBuilder.nonNullable.control('ACTIVE', Validators.required),
    startAt: this.formBuilder.control<Date | null>(this.nowLocal(), Validators.required),
    endAt: this.formBuilder.control<Date | null>(null),
    changeReason: this.formBuilder.nonNullable.control(''),
    marketSource: this.formBuilder.nonNullable.control(''),
    scopeType: this.formBuilder.nonNullable.control('ALL_STORES_AND_WEB'),
    physicalShopIds: this.formBuilder.nonNullable.control([] as number[]),
  });

  ngOnInit(): void {
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.formTick.update((n) => n + 1));
    this.searchInput
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.page.set(0);
        this.goToPageNumber.set(1);
        this.load();
      });
    this.permissionService.permissionsLoaded$.subscribe(() =>
      this.refreshPermissions(),
    );
    this.refreshPermissions();
    const sku = this.route.snapshot.queryParamMap.get('sku');
    if (sku) {
      this.skuCodeFilter.set(sku);
    }
    forkJoin({
      books: this.priceListService.listBooks(),
      policy: this.priceListService.listSellingPrices().pipe(catchError(() => of(null))),
    }).subscribe({
      next: ({ books, policy }) => {
        if (policy) {
          this.enabledSurfaces.set(
            new Set(policy.filter((item) => item.enabled).map((item) => item.code)),
          );
        }
        if (!this.visibleTabs().some((tab) => tab.code === this.activeCode())) {
          this.activeCode.set('RETAIL_DEFAULT');
        }
        this.books.set(books ?? []);
        this.load();
      },
      error: () => this.error.set('price_list.errors.load_books'),
    });
  }

  selectTab(code: string): void {
    if (this.activeCode() === code) {
      return;
    }
    this.activeCode.set(code);
    this.page.set(0);
    this.goToPageNumber.set(1);
    this.selectedIds.set(new Set());
    this.form.controls.priceType.setValue(this.tabPriceType());
    this.load();
  }

  isRowSelected(id: number): boolean {
    return this.selectedIds().has(id);
  }

  toggleRowSelection(id: number, checked: boolean): void {
    const next = new Set(this.selectedIds());
    if (checked) {
      next.add(id);
    } else {
      next.delete(id);
    }
    this.selectedIds.set(next);
  }

  toggleSelectAllPage(checked: boolean): void {
    const next = new Set(this.selectedIds());
    for (const item of this.items()) {
      if (checked) {
        next.add(item.id);
      } else {
        next.delete(item.id);
      }
    }
    this.selectedIds.set(next);
  }

  isBulkApprovable(item: PriceItem): boolean {
    if (!this.canApprovePrice()) {
      return false;
    }
    if (
      this.activeCode() === 'FLASH_SALE' &&
      item.pendingAmount != null &&
      item.pendingAmountStatus === 'PENDING'
    ) {
      return true;
    }
    return this.isPendingApproval(item);
  }

  isBulkDeactivatable(item: PriceItem): boolean {
    return this.canDeactivatePrice() && this.isLiveEffective(item);
  }

  isBulkCancellable(item: PriceItem): boolean {
    return this.canCancelPending() && this.isPendingApproval(item);
  }

  isBulkDeletable(item: PriceItem): boolean {
    return this.canDeletePrice() && this.isStopped(item);
  }

  clearanceScopeLabel(item: PriceItem): string {
    const scope =
      item.scopeType ||
      this.readConditionString(item.conditionJson, 'scopeType') ||
      'ALL_STORES_AND_WEB';
    if (scope === 'SELECTED_STORES') {
      const names =
        item.physicalShopNames?.filter(Boolean) ??
        this.readConditionStringArray(item.conditionJson, 'physicalShopNames');
      return names.length
        ? names.join(', ')
        : this.translate.instant('price_list.scope.selected_stores');
    }
    if (scope === 'WEB_ONLY') {
      return this.translate.instant('price_list.scope.web_only');
    }
    return this.translate.instant('price_list.scope.all_stores_and_web');
  }

  downloadImportTemplate(): void {
    const blob = buildPriceImportTemplateXlsx();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'price-list-import-template.xlsx';
    anchor.click();
    URL.revokeObjectURL(url);
    this.messages.add({
      severity: 'success',
      summary: this.translate.instant('price_list.download_template_success'),
    });
  }

  openImportDialog(): void {
    this.resetImportDialog();
    this.importDialogOpen.set(true);
  }

  closeImportDialog(): void {
    this.resetImportDialog();
    this.importDialogOpen.set(false);
    this.importBusy.set(false);
  }

  onImportFileChanged(file: FileUploadData | null): void {
    this.importUpload.set(file);
  }

  canSubmitImport(): boolean {
    return !!this.importUpload()?.file && !this.importBusy();
  }

  confirmImportFromFile(): void {
    const file = this.importUpload()?.file;
    if (!file) {
      this.messages.add({
        severity: 'warn',
        summary: this.translate.instant('price_list.import.file_required'),
      });
      return;
    }
    const ext = file.name.includes('.')
      ? file.name.split('.').pop()!.toLowerCase()
      : '';
    if (ext !== 'xlsx' && ext !== 'xls') {
      this.messages.add({
        severity: 'error',
        summary: this.translate.instant('price_list.import.invalid_file_type'),
      });
      return;
    }
    this.importBusy.set(true);
    this.priceListService.importPriceItems(file).subscribe({
      next: (result: PriceItemImportResult) => {
        this.importBusy.set(false);
        if ((result.failureCount ?? 0) > 0) {
          this.messages.add({
            severity: 'error',
            summary: this.translate.instant('price_list.import.error'),
            detail: this.formatImportError(result.errors?.[0]),
          });
          return;
        }
        this.closeImportDialog();
        this.messages.add({
          severity: 'success',
          summary: this.translate.instant('price_list.import.success'),
          detail: `${result.successCount}/${result.totalRows}`,
        });
        this.load();
      },
      error: (error: HttpErrorResponse) => {
        this.importBusy.set(false);
        const body = error.error as PriceItemImportResult | undefined;
        this.messages.add({
          severity: 'error',
          summary: this.translate.instant('price_list.import.error'),
          detail: this.formatImportError(body?.errors?.[0]),
        });
      },
    });
  }

  private formatImportError(
    error?: { sheet?: string | null; row?: number | null; message?: string | null } | null,
  ): string | undefined {
    if (!error?.message) {
      return undefined;
    }
    const message = this.translateImportMessage(error.message);
    if (!error.sheet) {
      return message;
    }
    const row = error.row ? ` #${error.row}` : '';
    return `${error.sheet}${row}: ${message}`;
  }

  private translateImportMessage(message: string): string {
    const exact: Record<string, string> = {
      'File is required': 'price_list.import.errors.file_required',
      'Missing required columns: SKU code and Amount':
        'price_list.import.errors.missing_columns',
      'No data rows found. Fill a data sheet such as Cost, Clearance, or Retail.':
        'price_list.import.errors.no_rows',
      'SKU code is required': 'price_list.import.errors.sku_required',
      'Amount is required': 'price_list.import.errors.amount_required',
      'Amount must be greater than zero': 'price_list.import.errors.amount_positive',
      'End time must be on or after the start time':
        'price_list.import.errors.end_before_start',
      'Invalid minimum quantity': 'price_list.import.errors.invalid_min_qty',
      'Shop code is required for selected-store scope':
        'price_list.import.errors.shop_required',
      'Start time is required': 'price_list.import.errors.start_required',
    };
    const key = exact[message];
    if (key) {
      return this.translate.instant(key);
    }
    const prefixed: { prefix: string; key: string }[] = [
      { prefix: 'Price book not found: ', key: 'price_list.import.errors.book_not_found' },
      { prefix: 'Failed to read Excel file: ', key: 'price_list.import.errors.read_failed' },
      { prefix: 'Unknown scope: ', key: 'price_list.import.errors.unknown_scope' },
      { prefix: 'Unknown shop code: ', key: 'price_list.import.errors.unknown_shop' },
      { prefix: 'Invalid amount: ', key: 'price_list.import.errors.invalid_amount' },
      {
        prefix: 'Invalid datetime (use dd/MM/yyyy HH:mm): ',
        key: 'price_list.import.errors.invalid_datetime',
      },
    ];
    for (const item of prefixed) {
      if (message.startsWith(item.prefix)) {
        return this.translate.instant(item.key, {
          value: message.slice(item.prefix.length),
        });
      }
    }
    return message;
  }

  private resetImportDialog(): void {
    this.importFileUpload?.reset();
    this.importUpload.set(null);
  }

  openBulkConfirm(action: 'approve' | 'deactivate' | 'cancel' | 'delete'): void {
    if (action === 'approve' && !this.canBulkApprove()) {
      return;
    }
    if (action === 'deactivate' && !this.canBulkDeactivate()) {
      return;
    }
    if (action === 'cancel' && !this.canBulkCancel()) {
      return;
    }
    if (action === 'delete' && !this.canBulkDelete()) {
      return;
    }
    this.bulkConfirmAction.set(action);
    this.bulkConfirmOpen.set(true);
  }

  confirmBulkAction(): void {
    const action = this.bulkConfirmAction();
    if (!action || this.bulkBusy()) {
      return;
    }
    const targets =
      action === 'approve'
        ? this.selectedItems().filter((item) => this.isBulkApprovable(item))
        : action === 'deactivate'
          ? this.selectedItems().filter((item) => this.isBulkDeactivatable(item))
          : action === 'delete'
            ? this.selectedItems().filter((item) => this.isBulkDeletable(item))
            : this.selectedItems().filter((item) => this.isBulkCancellable(item));
    if (!targets.length) {
      this.bulkConfirmOpen.set(false);
      return;
    }
    const itemIds = targets.map((item) => item.id);
    const request$ =
      action === 'approve'
        ? this.priceListService.bulkApproveItems(itemIds)
        : action === 'deactivate'
          ? this.priceListService.bulkDeactivateItems(itemIds)
          : action === 'delete'
            ? this.priceListService.bulkDeleteItems(itemIds)
            : this.priceListService.bulkCancelItems(itemIds);
    this.bulkBusy.set(true);
    request$.subscribe({
      next: (result) => {
        this.bulkBusy.set(false);
        this.bulkConfirmOpen.set(false);
        this.bulkConfirmAction.set(null);
        this.selectedIds.set(new Set());
        const succeeded = result?.succeededIds?.length ?? 0;
        if (succeeded > 0) {
          this.messages.add({
            severity: 'success',
            summary: this.translate.instant(this.bulkResultKey(action)),
          });
        } else {
          this.messages.add({
            severity: 'warn',
            summary: this.translate.instant('price_list.bulk.none_eligible'),
          });
        }
        this.load();
      },
      error: () => {
        this.bulkBusy.set(false);
        this.messages.add({
          severity: 'error',
          summary: this.translate.instant('price_list.bulk.failed'),
        });
      },
    });
  }

  bulkConfirmTitleKey(): string {
    switch (this.bulkConfirmAction()) {
      case 'approve':
        return 'price_list.bulk.confirm_approve_title';
      case 'deactivate':
        return 'price_list.bulk.confirm_deactivate_title';
      case 'delete':
        return 'price_list.bulk.confirm_delete_title';
      default:
        return 'price_list.bulk.confirm_cancel_title';
    }
  }

  bulkConfirmBodyKey(): string {
    switch (this.bulkConfirmAction()) {
      case 'approve':
        return 'price_list.bulk.confirm_approve';
      case 'deactivate':
        return 'price_list.bulk.confirm_deactivate';
      case 'delete':
        return 'price_list.bulk.confirm_delete';
      default:
        return 'price_list.bulk.confirm_cancel';
    }
  }

  private bulkResultKey(
    action: 'approve' | 'deactivate' | 'cancel' | 'delete',
  ): string {
    switch (action) {
      case 'approve':
        return 'price_list.bulk.approved';
      case 'deactivate':
        return 'price_list.bulk.deactivated';
      case 'delete':
        return 'price_list.bulk.deleted';
      default:
        return 'price_list.bulk.cancelled';
    }
  }

  private readConditionStringArray(
    conditionJson: string | null | undefined,
    key: string,
  ): string[] {
    try {
      if (!conditionJson) {
        return [];
      }
      const parsed = JSON.parse(conditionJson) as Record<string, unknown>;
      const value = parsed?.[key];
      if (!Array.isArray(value)) {
        return [];
      }
      return value.map((entry) => String(entry)).filter((entry) => entry.trim());
    } catch {
      return [];
    }
  }

  onTabKeydown(event: KeyboardEvent, index: number): void {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') {
      return;
    }
    event.preventDefault();
    const tabs = this.visibleTabs();
    const next =
      event.key === 'ArrowRight'
        ? (index + 1) % tabs.length
        : (index - 1 + tabs.length) % tabs.length;
    this.selectTab(tabs[next].code);
    const tabButtons = document.querySelectorAll<HTMLButtonElement>(
      '[role="tab"]',
    );
    tabButtons[next]?.focus();
  }

  onFilterChange(): void {
    this.searchInput.next();
  }

  clearSearch(): void {
    this.skuNameFilter.set('');
    this.skuCodeFilter.set('');
    this.amountFrom.set(null);
    this.amountTo.set(null);
    this.filterStartDate.set(null);
    this.filterEndDate.set(null);
    this.statusFilter.set(null);
    this.page.set(0);
    this.goToPageNumber.set(1);
    this.load();
  }

  /** Format a Date as yyyy-MM-dd for price-service query params. */
  private toFilterDate(value: Date | null): string | undefined {
    if (!value) {
      return undefined;
    }
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const d = String(value.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  onTablePage(event: PaginatorState): void {
    const nextPage = event.page ?? 0;
    const nextSize = event.rows ?? this.pageSize();
    this.goToPageNumber.set(nextPage + 1);
    if (nextPage === this.page() && nextSize === this.pageSize()) {
      return;
    }
    this.page.set(nextPage);
    this.pageSize.set(nextSize);
    this.load();
  }

  onGoToPage(): void {
    let target = Number(this.goToPageNumber());
    if (!Number.isFinite(target) || target < 1) {
      target = 1;
    } else if (target > this.maxPage()) {
      target = this.maxPage();
    }
    this.goToPageNumber.set(target);
    this.onTablePage({ page: target - 1, rows: this.pageSize() });
  }

  openCreate(): void {
    if (!this.allowCreate()) {
      return;
    }
    this.editingItem.set(null);
    this.form.reset({
      skuCode: '',
      priceType: this.tabPriceType(),
      amount: null,
      minQty: 50,
      ruleType: 'FIXED',
      ratePercent: 0,
      status: 'ACTIVE',
      startAt: this.nowLocal(),
      endAt: null,
      changeReason: '',
      marketSource: '',
      scopeType: 'ALL_STORES_AND_WEB',
      physicalShopIds: [],
    });
    this.form.controls.skuCode.enable();
    this.skuOptions.set([]);
    this.selectedSkuCost.set(null);
    this.selectedSkuRetail.set(null);
    this.applyCreateFieldLock(true);
    this.dialogOpen.set(true);
    this.loadSkuOptions();
    if (this.showClearanceScope()) {
      this.loadShops();
    }
  }

  private applyCreateFieldLock(locked: boolean): void {
    const keys = [
      'amount',
      'minQty',
      'ruleType',
      'ratePercent',
      'startAt',
      'endAt',
      'changeReason',
      'marketSource',
      'scopeType',
      'physicalShopIds',
    ] as const;
    for (const key of keys) {
      const control = this.form.controls[key];
      if (locked) {
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }
    }
  }

  private tabPriceType(): string {
    const types = this.activeTab().types;
    return types.find((type) => type !== 'BASE') ?? types[0];
  }

  private loadSkuOptions(): void {
    const book = this.activeBook();
    if (!book) {
      return;
    }
    this.skuOptionsLoading.set(true);
    this.priceListService.selectableSkus(book.id, this.tabPriceType()).subscribe({
      next: (options) => {
        this.skuOptions.set(options);
        this.skuOptionsLoading.set(false);
      },
      error: () => {
        this.skuOptionsLoading.set(false);
        this.messages.add({
          severity: 'error',
          summary: this.translate.instant('price_list.errors.load_skus'),
        });
      },
    });
  }

  openEdit(item: PriceItem): void {
    if (!this.canEditBasePrice() || this.isStopped(item)) {
      return;
    }
    this.editingItem.set(item);
    this.form.reset({
      skuCode: item.skuCode,
      priceType: item.priceType || 'BASE',
      amount: item.amount,
      minQty: item.minQty ?? 50,
      ruleType: 'FIXED',
      ratePercent: 0,
      status: 'ACTIVE',
      startAt: this.toLocalDate(item.startAt || item.effectiveFrom) ?? this.nowLocal(),
      endAt: this.toLocalDate(item.endAt || item.effectiveTo),
      changeReason: item.changeReason || '',
      marketSource: item.marketSource || '',
      scopeType: item.scopeType || 'ALL_STORES_AND_WEB',
      physicalShopIds: item.physicalShopIds ?? [],
    });
    this.form.controls.skuCode.disable();
    this.applyCreateFieldLock(false);
    this.loadSkuCost(item.skuCode);
    this.loadSkuRetail(item.skuCode);
    // Prefill rule from existing line when editable.
    try {
      const rule = item.ruleJson ? JSON.parse(item.ruleJson) : null;
      if (rule?.type) {
        this.form.patchValue({
          ruleType: rule.type,
          ratePercent:
            typeof rule.rate === 'number' ? Math.round(rule.rate * 100) : 0,
        });
      }
    } catch {
      /* keep defaults */
    }
    this.dialogOpen.set(true);
    if (this.showClearanceScope()) {
      this.loadShops();
    }
  }

  deactivate(item: PriceItem): void {
    this.priceListService.deactivateBasePrice(item.id).subscribe({
      next: () => {
        this.messages.add({
          severity: 'success',
          summary: this.translate.instant('price_list.deactivated'),
        });
        this.load();
      },
      error: (error: HttpErrorResponse) => this.showSaveError(error),
    });
  }

  save(): void {
    const book = this.activeBook();
    const editing = this.editingItem();
    this.form.controls.changeReason.clearValidators();
    this.form.controls.changeReason.updateValueAndValidity({ emitEvent: false });
    if (!book || !this.canSaveForm()) {
      this.form.markAllAsTouched();
      return;
    }
    const value = this.form.getRawValue();
    const now = Date.now();
    if (this.showDates() && !this.editingFlashSale()) {
      if (value.startAt && value.startAt.getTime() < now - 60_000) {
        this.messages.add({
          severity: 'error',
          summary: this.translate.instant('price_list.date_not_past'),
        });
        return;
      }
      if (value.endAt && value.endAt.getTime() < now - 60_000) {
        this.messages.add({
          severity: 'error',
          summary: this.translate.instant('price_list.date_not_past'),
        });
        return;
      }
      if (
        value.endAt &&
        value.startAt &&
        value.endAt.getTime() < value.startAt.getTime()
      ) {
        this.messages.add({
          severity: 'error',
          summary: this.translate.instant('price_list.end_before_start'),
        });
        return;
      }
    }
    if (
      this.showClearanceScope() &&
      value.scopeType === 'SELECTED_STORES' &&
      (!value.physicalShopIds || value.physicalShopIds.length === 0)
    ) {
      this.messages.add({
        severity: 'error',
        summary: this.translate.instant('price_list.errors.stores_required'),
      });
      return;
    }
    if (this.activeCode() === 'COST_DEFAULT') {
      if (!(Number(value.amount) > 0)) {
        this.form.markAllAsTouched();
        this.messages.add({
          severity: 'error',
          summary: this.translate.instant('price_list.errors.cost_amount'),
        });
        return;
      }
      if (editing) {
        this.saveExisting(editing);
        return;
      }
      this.createRow(book, value);
      return;
    }
    if (editing) {
      this.saveExisting(editing);
      return;
    }
    if (value.ruleType !== 'FIXED') {
      this.createRow(book, value);
      return;
    }
    const amount = Number(value.amount);
    this.saving.set(true);
    this.priceListService.publishedAmount('COST_DEFAULT', 'COST', value.skuCode).subscribe({
      next: (cost) => {
        if (cost == null) {
          this.saving.set(false);
          this.messages.add({
            severity: 'error',
            summary: this.translate.instant('price_list.errors.cost_required'),
          });
          return;
        }
        if (!(amount > cost)) {
          this.saving.set(false);
          this.messages.add({
            severity: 'error',
            summary: this.translate.instant('price_list.errors.above_cost'),
          });
          return;
        }
        this.continueCreateValidations(book, value, amount);
      },
      error: (error: HttpErrorResponse) => this.showSaveError(error),
    });
  }

  private continueCreateValidations(
    book: PriceBook,
    value: ReturnType<typeof this.form.getRawValue>,
    amount: number,
  ): void {
    if (value.priceType === 'WHOLESALE' || value.priceType === 'CLEARANCE') {
      this.checkAgainstPublished(
        'RETAIL_DEFAULT',
        'BASE',
        value.skuCode,
        amount,
        'price_list.errors.retail_required',
        value.priceType === 'WHOLESALE'
          ? 'price_list.errors.wholesale_not_below_retail'
          : 'price_list.errors.clearance_not_below_retail',
        (left, reference) => left < reference,
        () => {
          if (value.priceType !== 'WHOLESALE') {
            this.createRow(book, value);
            return;
          }
          this.priceListService
            .publishedAmount('DISTRIBUTION', 'DISTRIBUTION', value.skuCode)
            .subscribe({
              next: (reference) => {
                if (reference != null && !(amount < reference)) {
                  this.saving.set(false);
                  this.messages.add({
                    severity: 'error',
                    summary: this.translate.instant(
                      'price_list.errors.wholesale_not_below_distribution',
                    ),
                  });
                  return;
                }
                this.createRow(book, value);
              },
              error: (error: HttpErrorResponse) => this.showSaveError(error),
            });
        },
      );
      return;
    }
    if (value.priceType === 'DISTRIBUTION') {
      this.checkAgainstPublished(
        'WHOLESALE',
        'WHOLESALE',
        value.skuCode,
        amount,
        'price_list.errors.distribution_needs_wholesale',
        'price_list.errors.distribution_not_above_wholesale',
        (left, reference) => left > reference,
        () =>
          this.checkAgainstPublished(
            'RETAIL_DEFAULT',
            'BASE',
            value.skuCode,
            amount,
            'price_list.errors.retail_required',
            'price_list.errors.distribution_not_below_retail',
            (left, reference) => left < reference,
            () => this.createRow(book, value),
          ),
      );
      return;
    }
    this.createRow(book, value);
  }

  private checkAgainstPublished(
    bookCode: string,
    priceType: string,
    skuCode: string,
    amount: number,
    missingKey: string,
    invalidKey: string,
    valid: (amount: number, reference: number) => boolean,
    onValid: () => void,
  ): void {
    this.saving.set(true);
    this.priceListService.publishedAmount(bookCode, priceType, skuCode).subscribe({
      next: (reference) => {
        if (reference == null) {
          this.saving.set(false);
          this.messages.add({
            severity: 'error',
            summary: this.translate.instant(missingKey),
          });
          return;
        }
        if (!valid(amount, reference)) {
          this.saving.set(false);
          this.messages.add({
            severity: 'error',
            summary: this.translate.instant(invalidKey),
          });
          return;
        }
        onValid();
      },
      error: (error: HttpErrorResponse) => this.showSaveError(error),
    });
  }

  private saveExisting(item: PriceItem): void {
    const value = this.form.getRawValue();
    this.saving.set(true);
    if (this.activeCode() === 'FLASH_SALE' || item.priceType === 'FLASH_SALE') {
      this.flashSaleApi.requestAmount(item.id, Number(value.amount)).subscribe({
        next: () => this.afterSave(),
        error: (error: HttpErrorResponse) => this.showSaveError(error),
      });
      return;
    }
    const rule =
      !this.activeTab().allowRule || value.ruleType === 'FIXED'
        ? JSON.stringify({ type: 'FIXED' })
        : JSON.stringify({
            type: value.ruleType,
            rate: Number(value.ratePercent || 0) / 100,
            base: value.ruleType === 'MARKUP_ON_COST' ? 'COST' : 'BASE',
          });
    const shopNames =
      value.scopeType === 'SELECTED_STORES'
        ? this.shopOptions()
            .filter((shop) => value.physicalShopIds.includes(shop.id))
            .map((shop) => shop.name)
        : undefined;
    const startAt =
      this.showDates() && value.startAt
        ? value.startAt
        : this.toLocalDate(item.startAt || item.effectiveFrom) ?? this.nowLocal();
    const endAt =
      this.showDates()
        ? value.endAt
        : this.toLocalDate(item.endAt || item.effectiveTo);
    this.priceListService
      .updateItem(item.id, {
        revisionId: item.revisionId ?? 0,
        skuCode: item.skuCode,
        priceType: item.priceType || value.priceType,
        amount: value.ruleType === 'FIXED' ? Number(value.amount) : Number(value.amount) || 0,
        currencyCode: item.currencyCode || 'VND',
        effectiveFrom: this.effectiveFrom(startAt),
        startAt: this.toApiDateTime(startAt),
        endAt: endAt ? this.toApiDateTime(endAt) : null,
        ruleJson: rule,
        status: 'ACTIVE',
        minQty: value.priceType === 'WHOLESALE' ? Number(value.minQty) || 50 : undefined,
        changeReason: value.changeReason,
        marketSource:
          value.priceType === 'MARKET' ? value.marketSource?.trim() || null : null,
        scopeType: value.priceType === 'CLEARANCE' ? value.scopeType : undefined,
        physicalShopIds:
          value.priceType === 'CLEARANCE' && value.scopeType === 'SELECTED_STORES'
            ? value.physicalShopIds
            : undefined,
        physicalShopNames: shopNames,
        physicalShopCodes: this.selectedShopCodes(value),
      })
      .subscribe({
        next: () => this.afterSave(),
        error: (error: HttpErrorResponse) => this.showSaveError(error),
      });
  }

  approveFlashAmount(item: PriceItem): void {
    this.flashSaleApi.approveAmount(item.id).subscribe({
      next: () => {
        this.messages.add({
          severity: 'success',
          summary: this.translate.instant('price_list.approved'),
        });
        this.load();
      },
      error: (error: HttpErrorResponse) => this.showSaveError(error),
    });
  }

  scheduleLabel(item: PriceItem): string {
    if (item.status === 'INACTIVE') {
      return 'price_list.schedule_expired';
    }
    if (!item.publishedFlag) {
      return 'price_list.status_pending';
    }
    if (item.scheduleStatus === 'EXPIRED') {
      return 'price_list.schedule_expired';
    }
    if (item.scheduleStatus === 'SCHEDULED') {
      return 'price_list.schedule_scheduled';
    }
    return 'price_list.status_active';
  }

  marketplaceShopId(item: PriceItem): number | null {
    const fromCondition = this.readConditionNumber(item.conditionJson, 'shopId');
    if (fromCondition != null) {
      return fromCondition;
    }
    return null;
  }

  marketplaceChannelLabel(item: PriceItem): string {
    const shopName = this.readConditionString(item.conditionJson, 'shopName');
    const channel = item.channelCode?.trim();
    if (shopName && channel) {
      return `${channel} – ${shopName}`;
    }
    return shopName || channel || '—';
  }

  private readConditionNumber(
    conditionJson: string | null | undefined,
    key: string,
  ): number | null {
    try {
      if (!conditionJson) {
        return null;
      }
      const parsed = JSON.parse(conditionJson) as Record<string, unknown>;
      const value = parsed?.[key];
      const num = typeof value === 'number' ? value : Number(value);
      return Number.isFinite(num) ? num : null;
    } catch {
      return null;
    }
  }

  private readConditionString(
    conditionJson: string | null | undefined,
    key: string,
  ): string | null {
    try {
      if (!conditionJson) {
        return null;
      }
      const parsed = JSON.parse(conditionJson) as Record<string, unknown>;
      const value = parsed?.[key];
      return typeof value === 'string' && value.trim() ? value.trim() : null;
    } catch {
      return null;
    }
  }

  isPendingApproval(item: PriceItem): boolean {
    return item.status !== 'INACTIVE' && !item.publishedFlag;
  }

  isLiveEffective(item: PriceItem): boolean {
    return (
      item.status === 'ACTIVE' &&
      !!item.publishedFlag &&
      item.scheduleStatus !== 'EXPIRED' &&
      item.scheduleStatus !== 'SCHEDULED'
    );
  }

  /** Ngừng hiệu lực: deactivated, or the effective window has ended. */
  isStopped(item: PriceItem): boolean {
    return item.status === 'INACTIVE' || item.scheduleStatus === 'EXPIRED';
  }

  editingFlashSale(): boolean {
    return this.activeCode() === 'FLASH_SALE' && this.editingItem() != null;
  }

  deleteStopped(item: PriceItem): void {
    if (!this.canDeletePrice() || !this.isStopped(item)) {
      return;
    }
    this.priceListService.deleteItem(item.id).subscribe({
      next: () => {
        this.messages.add({
          severity: 'success',
          summary: this.translate.instant('price_list.deleted'),
        });
        this.load();
      },
      error: (error: HttpErrorResponse) => this.showSaveError(error),
    });
  }

  onSkuSelected(skuCode: string | null): void {
    const code = skuCode?.trim() || null;
    this.applyCreateFieldLock(!code);
    this.formTick.update((n) => n + 1);
    this.loadSkuCost(code);
    this.loadSkuRetail(code);
  }

  private loadSkuCost(skuCode: string | null | undefined): void {
    const code = skuCode?.trim();
    if (!code) {
      this.selectedSkuCost.set(null);
      this.selectedSkuCostLoading.set(false);
      return;
    }
    this.selectedSkuCostLoading.set(true);
    this.priceListService.publishedAmount('COST_DEFAULT', 'COST', code).subscribe({
      next: (cost) => {
        this.selectedSkuCost.set(cost);
        this.selectedSkuCostLoading.set(false);
      },
      error: () => {
        this.selectedSkuCost.set(null);
        this.selectedSkuCostLoading.set(false);
      },
    });
  }

  private loadSkuRetail(skuCode: string | null | undefined): void {
    const code = skuCode?.trim();
    if (!code || !this.showRetailReference()) {
      this.selectedSkuRetail.set(null);
      this.selectedSkuRetailLoading.set(false);
      return;
    }
    this.selectedSkuRetailLoading.set(true);
    this.priceListService.publishedAmount('RETAIL_DEFAULT', 'BASE', code).subscribe({
      next: (retail) => {
        this.selectedSkuRetail.set(retail);
        this.selectedSkuRetailLoading.set(false);
        this.formTick.update((n) => n + 1);
      },
      error: () => {
        this.selectedSkuRetail.set(null);
        this.selectedSkuRetailLoading.set(false);
        this.formTick.update((n) => n + 1);
      },
    });
  }

  approveItem(item: PriceItem): void {
    if (!this.canApprovePrice() || this.approving()) {
      return;
    }
    this.approving.set(true);
    this.priceListService.approveItem(item.id).subscribe({
      next: () => {
        this.approving.set(false);
        this.messages.add({
          severity: 'success',
          summary: this.translate.instant('price_list.approved'),
        });
        this.load();
      },
      error: (error: HttpErrorResponse) => {
        this.approving.set(false);
        this.messages.add({
          severity: 'error',
          summary: this.translate.instant(this.saveErrorKey(error, true)),
        });
      },
    });
  }

  cancelPending(item: PriceItem): void {
    if (!this.canCancelPending()) {
      return;
    }
    this.priceListService.deleteItem(item.id).subscribe({
      next: () => {
        this.messages.add({
          severity: 'success',
          summary: this.translate.instant('price_list.cancelled_pending'),
        });
        this.load();
      },
      error: (error: HttpErrorResponse) => this.showSaveError(error),
    });
  }

  deactivateItem(item: PriceItem): void {
    if (!this.canDeactivatePrice()) {
      return;
    }
    this.priceListService.deactivateItem(item.id).subscribe({
      next: () => {
        this.messages.add({
          severity: 'success',
          summary: this.translate.instant('price_list.deactivated'),
        });
        this.load();
      },
      error: (error: HttpErrorResponse) => this.showSaveError(error),
    });
  }

  private nowLocal(): Date {
    return new Date();
  }

  private toLocalDate(value?: string | null): Date | null {
    if (!value) {
      return null;
    }
    const normalized = value.includes('T') ? value : `${value}T00:00:00`;
    const date = new Date(normalized);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  /** Send LocalDateTime without timezone (Asia/Ho_Chi_Minh on server). */
  private toApiDateTime(value: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}T${pad(value.getHours())}:${pad(value.getMinutes())}:${pad(value.getSeconds())}`;
  }

  private effectiveFrom(startAt: Date): string {
    return `${this.toApiDateTime(startAt)}+07:00`;
  }

  private loadShops(): void {
    if (this.shopOptions().length > 0) {
      return;
    }
    // Channel client Accept is */*, so HttpClient may deliver a Blob (same as goods SKU page).
    this.physicalShopApi
      .showPage({
        pageRequest: { page: 0, size: 500, sortBy: 'id', ascending: false },
      })
      .pipe(
        switchMap((payload) => from(BlobConverterUtil.convertToJson(payload))),
        map((raw) => this.shopRowsFromPage(raw)),
      )
      .subscribe({
        next: (rows) => {
          this.shopOptions.set(
            rows
              .filter((row) => row.id != null)
              .map((row) => ({
                id: row.id!,
                name: (row.name ?? row.code ?? String(row.id)).trim() || String(row.id),
                code: (row.code ?? '').trim(),
              })),
          );
        },
        error: () => this.shopOptions.set([]),
      });
  }

  private shopRowsFromPage(raw: unknown): PhysicalShopVO[] {
    if (Array.isArray(raw)) {
      return raw as PhysicalShopVO[];
    }
    const page = (raw ?? {}) as { pageContent?: PhysicalShopVO[] };
    return page.pageContent ?? [];
  }

  private selectedShopCodes(value: {
    scopeType: string;
    physicalShopIds: number[];
    priceType: string;
  }): string[] | undefined {
    if (value.priceType !== 'CLEARANCE' || value.scopeType !== 'SELECTED_STORES') {
      return undefined;
    }
    const codes = this.shopOptions()
      .filter((shop) => value.physicalShopIds.includes(shop.id) && shop.code)
      .map((shop) => shop.code);
    return codes.length ? codes : undefined;
  }

  private createRow(
    book: PriceBook,
    value: ReturnType<typeof this.form.getRawValue>,
  ): void {
    const rule =
      !this.activeTab().allowRule || value.ruleType === 'FIXED'
        ? JSON.stringify({ type: 'FIXED' })
        : JSON.stringify({
            type: value.ruleType,
            rate: Number(value.ratePercent || 0) / 100,
            base: value.ruleType === 'MARKUP_ON_COST' ? 'COST' : 'BASE',
          });
    const shopNames =
      value.scopeType === 'SELECTED_STORES'
        ? this.shopOptions()
            .filter((shop) => value.physicalShopIds.includes(shop.id))
            .map((shop) => shop.name)
        : undefined;
    this.saving.set(true);
    const startAt = value.startAt ?? this.nowLocal();
    const payload = {
      skuCode: value.skuCode.trim(),
      priceType: value.priceType,
      amount: value.ruleType === 'FIXED' ? Number(value.amount) : 0,
      currencyCode: book.currencyCode || 'VND',
      effectiveFrom: this.effectiveFrom(startAt),
      startAt: this.toApiDateTime(startAt),
      endAt: value.endAt ? this.toApiDateTime(value.endAt) : null,
      ruleJson: rule,
      status: value.status,
      changeReason: value.changeReason,
      marketSource:
        value.priceType === 'MARKET' ? value.marketSource?.trim() || null : null,
      scopeType: value.priceType === 'CLEARANCE' ? value.scopeType : undefined,
      physicalShopIds:
        value.priceType === 'CLEARANCE' && value.scopeType === 'SELECTED_STORES'
          ? value.physicalShopIds
          : undefined,
      physicalShopNames: shopNames,
      physicalShopCodes: this.selectedShopCodes(value),
      minQty: value.priceType === 'WHOLESALE' ? Number(value.minQty) || 50 : undefined,
    };
    const create =
      this.activeCode() === 'RETAIL_DEFAULT' && value.priceType === 'BASE'
        ? this.priceListService.createBasePrice(book.id, {
            ...payload,
            priceType: 'BASE',
          })
        : this.priceListService.createPrice(book.id, payload);
    create.subscribe({
      next: () => this.afterSave(),
      error: (error: HttpErrorResponse) => this.showSaveError(error),
    });
  }

  vendorLabel(vendorId?: number | null): string {
    if (vendorId == null) {
      return this.translate.instant('price_list.vendor_missing');
    }
    return this.vendorLabels()[vendorId] ?? String(vendorId);
  }

  programLabel(ruleJson?: string): string {
    if (!ruleJson) {
      return this.translate.instant('price_list.no_price');
    }
    try {
      const rule = JSON.parse(ruleJson) as {
        program?: string;
        programName?: string;
      };
      const name = (rule.programName || rule.program || '').trim();
      return name || this.translate.instant('price_list.no_price');
    } catch {
      return this.translate.instant('price_list.no_price');
    }
  }

  ruleLabel(ruleJson?: string): string {
    if (!ruleJson) {
      return this.translate.instant('price_list.rules.FIXED');
    }
    try {
      const rule = JSON.parse(ruleJson) as { type?: string; rate?: number };
      const type = rule.type || 'FIXED';
      const label = this.translate.instant(`price_list.rules.${type}`);
      if (rule.rate == null) {
        return label;
      }
      return `${label} ${Math.round(rule.rate * 100)}%`;
    } catch {
      return ruleJson;
    }
  }

  submit(): void {
    const book = this.activeBook();
    if (!book || this.approving()) {
      return;
    }
    this.approving.set(true);
    this.priceListService.submitLatest(book.id).subscribe({
      next: (revision) => {
        this.approving.set(false);
        this.messages.add({
          severity: revision ? 'success' : 'info',
          summary: this.translate.instant(
            revision ? 'price_list.submitted' : 'price_list.submit_empty',
          ),
        });
        if (revision) {
          this.load();
        }
      },
      error: (error: HttpErrorResponse) => {
        this.approving.set(false);
        this.messages.add({
          severity: 'error',
          summary: this.translate.instant(this.saveErrorKey(error, true)),
        });
      },
    });
  }

  approve(): void {
    const book = this.activeBook();
    if (!book || this.approving()) {
      return;
    }
    this.approving.set(true);
    this.priceListService.approveAndPublish(book.id).subscribe({
      next: (revision) => {
        this.approving.set(false);
        this.messages.add({
          severity: revision ? 'success' : 'info',
          summary: this.translate.instant(
            revision ? 'price_list.approved' : 'price_list.approve_empty',
          ),
        });
        if (revision) {
          this.load();
        }
      },
      error: (error: HttpErrorResponse) => {
        this.approving.set(false);
        this.messages.add({
          severity: 'error',
          summary: this.translate.instant(this.saveErrorKey(error, true)),
        });
      },
    });
  }

  private refreshPermissions(): void {
    this.canEditBasePrice.set(
      this.permissionService.canDoAction(
        'product.product_pricing.edit_base_price',
      ),
    );
    this.canCreatePrice.set(
      this.permissionService.canDoAction('product.product_pricing.create'),
    );
    this.canDeletePrice.set(
      this.permissionService.canDoAction('product.product_pricing.delete'),
    );
    this.canApprovePrice.set(
      this.permissionService.canDoAction('product.product_pricing.approve'),
    );
    this.canCancelPending.set(
      this.permissionService.canDoAction('product.product_pricing.cancel'),
    );
    this.canDeactivatePrice.set(
      this.permissionService.canDoAction('product.product_pricing.deactivate'),
    );
  }

  private afterSave(): void {
    this.saving.set(false);
    this.dialogOpen.set(false);
    this.editingItem.set(null);
    this.messages.add({
      severity: 'success',
      summary: this.translate.instant('price_list.saved'),
    });
    this.load();
  }

  private showSaveError(error: HttpErrorResponse): void {
    this.saving.set(false);
    this.messages.add({
      severity: 'error',
      summary: this.translate.instant(this.saveErrorKey(error, false)),
    });
  }

  private saveErrorKey(error: HttpErrorResponse, approve: boolean): string {
    const message = String(error.error?.message ?? '');
    if (message.includes('cannot approve their own')) {
      return 'price_list.errors.approve_self';
    }
    if (message.includes('must be submitted before approval')) {
      return 'price_list.errors.approve_not_submitted';
    }
    if (message.includes('Authenticated user is required')) {
      return 'price_list.errors.actor_required';
    }
    if (error.status === 403) {
      return approve
        ? 'price_list.errors.approve_forbidden'
        : 'price_list.errors.forbidden';
    }
    if (message.includes('greater than the sales price')) {
      return 'price_list.errors.market_below_sales';
    }
    if (message.includes('requires a published sales price')) {
      return 'price_list.errors.market_needs_sales';
    }
    if (message.includes('greater than the wholesale price')) {
      return 'price_list.errors.distribution_not_above_wholesale';
    }
    if (message.includes('requires a published wholesale price')) {
      return 'price_list.errors.distribution_needs_wholesale';
    }
    if (message.includes('lower than the distribution price')) {
      return 'price_list.errors.wholesale_not_below_distribution';
    }
    if (message.includes('lower than the retail price') && message.includes('Wholesale')) {
      return 'price_list.errors.wholesale_not_below_retail';
    }
    if (message.includes('lower than the retail price') && message.includes('Clearance')) {
      return 'price_list.errors.clearance_not_below_retail';
    }
    if (message.includes('lower than the retail price') && message.includes('Distribution')) {
      return 'price_list.errors.distribution_not_below_retail';
    }
    if (message.includes('greater than the cost price')) {
      return 'price_list.errors.above_cost';
    }
    if (message.includes('Cost price is required')) {
      return 'price_list.errors.cost_required';
    }
    if (message.includes('Retail price is required')) {
      return 'price_list.errors.retail_required';
    }
    if (message.includes('Change reason is required')) {
      return 'price_list.errors.reason_required';
    }
    if (message.includes('At least one store')) {
      return 'price_list.errors.stores_required';
    }
    if (message.includes('Cost price is recorded')) {
      return 'price_list.cost_from_purchase';
    }
    if (message.includes('already exists for this SKU')) {
      return 'price_list.errors.sku_already_priced';
    }
    return approve ? 'price_list.errors.approve' : 'price_list.errors.save';
  }

  private load(): void {
    const book = this.activeBook();
    if (!book) {
      this.items.set([]);
      this.total.set(0);
      return;
    }
    this.loading.set(true);
    this.error.set(null);
    const types = this.activeTab().types;
    this.priceListService
      .pageItems(book.id, {
        page: this.page(),
        size: this.pageSize(),
        skuCode: this.skuCodeFilter().trim() || undefined,
        skuName: this.skuNameFilter().trim() || undefined,
        amountFrom: this.amountFrom() ?? undefined,
        amountTo: this.amountTo() ?? undefined,
        startDate: this.toFilterDate(this.filterStartDate()),
        endDate: this.toFilterDate(this.filterEndDate()),
        priceType: types.length === 1 ? types[0] : undefined,
        lifecycleStatus: this.statusFilter() ?? undefined,
      })
      .subscribe({
        next: (page) => {
          const content = page?.pageContent ?? [];
          this.items.set(content);
          this.total.set(page?.totalElements ?? 0);
          this.loading.set(false);
          if (this.activeCode() === 'COST_DEFAULT') {
            this.resolveVendorLabels(content);
          }
        },
        error: () => {
          this.loading.set(false);
          this.error.set('price_list.errors.load_items');
        },
      });
  }

  private resolveVendorLabels(items: PriceItem[]): void {
    const known = this.vendorLabels();
    const missing = [
      ...new Set(
        items
          .map((item) => item.vendorId)
          .filter((id): id is number => id != null && known[id] == null),
      ),
    ];
    if (!missing.length) {
      return;
    }
    forkJoin(
      missing.map((id) =>
        this.vendorApi.getById(id).pipe(
          switchMap((payload) => from(BlobConverterUtil.convertToJson(payload))),
          catchError(() => of(null)),
        ),
      ),
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((vendors) => {
        const next = { ...this.vendorLabels() };
        vendors.forEach((vendor, index) => {
          const id = missing[index];
          if (vendor?.vendorCode || vendor?.vendorName) {
            next[id] = [vendor.vendorCode, vendor.vendorName]
              .filter(Boolean)
              .join(' – ');
          }
        });
        this.vendorLabels.set(next);
      });
  }
}
